// DidYouKnowSection — vintage travel-postcard slider.
//
// Each curiosity is rendered as a hand-tilted postcard pinned to the page
// with washi tape, divided into a handwritten letter side (left) and a
// stamp + cancel-mark side (right). One postcard at a time, navigated via
// arrow buttons + pagination dots. Edit mode lets visitors add / delete /
// edit / reorder postcards, persisted per `hotel_slug`.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronLeft, ChevronRight, Plus, Trash2, ArrowLeft, ArrowRight,
  Pencil, X, Save, Loader2, Send, Database,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEditMode } from "@/contexts/CmsContext";
import MediaPicker from "@/components/admin/MediaPicker";
import BunnyStoragePicker from "@/components/admin/BunnyStoragePicker";

const API = process.env.REACT_APP_BACKEND_URL || "";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || obj.es || "";
};

const safeReadBody = async (res) => {
  try { return await res.json(); } catch { return null; }
};

// Slight per-card tilt to mimic postcards pinned to a wall.
const TILTS = ["-1.2deg", "0.7deg", "-0.4deg", "1deg", "-0.8deg"];
const tiltFor = (idx) => TILTS[idx % TILTS.length];

// ── A single read-only postcard ──────────────────────────────────────────
const PostcardArt = ({ postcard, lang, indexTilt = 0 }) => {
  const locationLabel = pick(postcard.location_label, lang) || pick(postcard.location_label, "en");
  const title = pick(postcard.title, lang) || pick(postcard.title, "en");
  const greeting = pick(postcard.greeting, lang) || pick(postcard.greeting, "en");
  const body = pick(postcard.body, lang) || pick(postcard.body, "en");
  const signoff = pick(postcard.signoff, lang) || pick(postcard.signoff, "en");
  const caption = pick(postcard.caption, lang) || pick(postcard.caption, "en");
  const signature = postcard.signature || "";

  return (
    <div
      className="relative mx-auto w-full max-w-[920px] select-none"
      style={{ transform: `rotate(${tiltFor(indexTilt)})` }}
      data-testid={`postcard-${postcard.id}`}
      data-no-xaluca-logo="true"
    >
      {/* Washi tape — top-left + top-right */}
      <span
        className="absolute -top-3 left-12 w-24 h-7 z-10"
        style={{
          background: "linear-gradient(180deg, rgba(232,213,180,0.85), rgba(212,194,158,0.85))",
          boxShadow: "0 2px 4px rgba(28,22,18,0.08)",
          transform: "rotate(-6deg)",
        }}
        aria-hidden="true"
      />
      <span
        className="absolute -top-3 right-16 w-24 h-7 z-10"
        style={{
          background: "linear-gradient(180deg, rgba(232,213,180,0.85), rgba(212,194,158,0.85))",
          boxShadow: "0 2px 4px rgba(28,22,18,0.08)",
          transform: "rotate(5deg)",
        }}
        aria-hidden="true"
      />

      {/* Postcard paper */}
      <article
        className="relative bg-[#F2E8D4] overflow-hidden"
        style={{
          // Soft drop shadow + subtle paper grain.
          boxShadow:
            "0 30px 60px -25px rgba(28,22,18,0.25), 0 12px 24px -15px rgba(28,22,18,0.15)",
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220' viewBox='0 0 220 220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.55  0 0 0 0 0.46  0 0 0 0 0.35  0 0 0 0.05 0'/></filter><rect width='220' height='220' filter='url(%23n)' opacity='0.55'/></svg>\")",
          backgroundSize: "220px 220px",
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr] gap-0 p-7 md:p-12">
          {/* ── LEFT — letter side ────────────────────────────────────── */}
          <div className="relative">
            {/* Header row */}
            <div className="flex items-start justify-between gap-4 mb-6 md:mb-8">
              {locationLabel ? (
                <p className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#7C5A3F]">
                  <Send className="w-3 h-3" strokeWidth={1.8} />
                  {locationLabel}
                </p>
              ) : <span />}
              {title ? (
                <p className="font-handwritten text-2xl md:text-3xl text-[#C16542] leading-tight md:hidden">
                  {title}
                </p>
              ) : null}
            </div>

            {/* Dotted separator under the header */}
            <div
              className="border-t border-dashed border-[#7C5A3F]/35 mb-7 md:mb-9"
              aria-hidden="true"
            />

            {/* Letter body */}
            {greeting ? (
              <p
                className="font-handwritten text-3xl md:text-4xl text-[#2C2621] leading-snug mb-4 md:mb-6"
                data-testid={`postcard-greeting-${postcard.id}`}
              >
                {greeting}
              </p>
            ) : null}
            {body ? (
              <p
                className="font-handwritten text-xl md:text-2xl text-[#2C2621] leading-[1.55] whitespace-pre-line"
                data-testid={`postcard-body-${postcard.id}`}
              >
                {body}
              </p>
            ) : null}
            {signoff ? (
              <p
                className="font-handwritten text-xl md:text-2xl text-[#5C7C4F] leading-snug mt-8 md:mt-10"
                data-testid={`postcard-signoff-${postcard.id}`}
              >
                {signoff}
              </p>
            ) : null}
            {signature ? (
              <p
                className="font-signature text-4xl md:text-6xl text-[#C16542] leading-none mt-3"
                data-testid={`postcard-signature-${postcard.id}`}
              >
                {signature}
              </p>
            ) : null}
          </div>

          {/* Mobile divider before the stamp column */}
          <div
            className="md:hidden border-t border-dashed border-[#7C5A3F]/35 my-8"
            aria-hidden="true"
          />

          {/* ── RIGHT — stamp side ───────────────────────────────────── */}
          <div className="relative md:pl-10">
            {/* Vertical dashed divider (desktop only) */}
            <div
              className="hidden md:block absolute left-0 top-0 bottom-0 border-l border-dashed border-[#7C5A3F]/35"
              aria-hidden="true"
            />

            {/* Title (desktop) */}
            {title ? (
              <p className="hidden md:block font-handwritten text-3xl lg:text-4xl text-[#C16542] leading-tight mb-6 text-right">
                {title}
              </p>
            ) : null}

            {/* Stamp + cancel mark */}
            <div className="relative w-fit ml-auto">
              {/* Postal cancel mark — slightly larger, anchored so it overlaps the bottom-left of the stamp */}
              <svg
                className="absolute -left-8 -bottom-6 w-36 h-36 md:w-40 md:h-40 pointer-events-none z-10 opacity-80"
                viewBox="0 0 120 120"
                aria-hidden="true"
              >
                <g
                  fill="none"
                  stroke="#5C5248"
                  strokeWidth="1.4"
                  strokeOpacity="0.85"
                  transform="rotate(-12 60 60)"
                >
                  <circle cx="60" cy="60" r="50" />
                  <circle cx="60" cy="60" r="40" />
                </g>
                <g
                  transform="rotate(-12 60 60)"
                  fill="#5C5248"
                  fillOpacity="0.85"
                  fontFamily="Outfit, sans-serif"
                  fontSize="7.5"
                  fontWeight="500"
                  letterSpacing="2.5"
                >
                  <path id={`top-arc-${postcard.id}`} d="M 16 60 A 44 44 0 0 1 104 60" fill="transparent" />
                  <text textAnchor="middle">
                    <textPath href={`#top-arc-${postcard.id}`} startOffset="50%">XALUCA TOURS</textPath>
                  </text>
                  <path id={`bot-arc-${postcard.id}`} d="M 16 60 A 44 44 0 0 0 104 60" fill="transparent" />
                  <text textAnchor="middle">
                    <textPath href={`#bot-arc-${postcard.id}`} startOffset="50%">MERZOUGA</textPath>
                  </text>
                </g>
              </svg>

              {/* Stamp — perforated edges via dashed border */}
              <div
                className="relative w-32 h-40 md:w-36 md:h-44 bg-[#FDFBF7] p-1.5"
                style={{
                  boxShadow: "0 4px 12px rgba(28,22,18,0.18)",
                  // create a stamped/perforated edge using radial-gradient dots
                  backgroundImage:
                    "radial-gradient(circle at top left, transparent 2px, #FDFBF7 2.5px), radial-gradient(circle at top right, transparent 2px, #FDFBF7 2.5px), radial-gradient(circle at bottom left, transparent 2px, #FDFBF7 2.5px), radial-gradient(circle at bottom right, transparent 2px, #FDFBF7 2.5px)",
                  backgroundSize: "8px 8px",
                  backgroundRepeat: "repeat-x, repeat-x, repeat-x, repeat-x",
                  backgroundPosition: "top left, top right, bottom left, bottom right",
                }}
              >
                <div className="relative w-full h-full overflow-hidden bg-[#A07042]" data-no-cms-edit="true">
                  {postcard.image_url ? (
                    <img
                      src={postcard.image_url}
                      alt={title || ""}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : null}
                </div>
              </div>
            </div>

            {/* Caption under the stamp */}
            {caption ? (
              <p
                className="font-handwritten text-lg md:text-xl text-[#5C5248] mt-5 text-right"
                data-testid={`postcard-caption-${postcard.id}`}
              >
                {caption}
              </p>
            ) : null}

            {/* Small Amazigh diamond ornament at the bottom-right */}
            <svg
              className="absolute bottom-2 right-2 w-5 h-5 text-[#7C5A3F]/40"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                d="M10 1 L14 5 L10 9 L6 5 Z M10 11 L14 15 L10 19 L6 15 Z"
                fill="currentColor"
              />
            </svg>
          </div>
        </div>
      </article>
    </div>
  );
};

// ── Editor dialog ────────────────────────────────────────────────────────
const PostcardEditor = ({ postcard, onSave, onClose }) => {
  const blankI18n = { en: "", fr: "", es: "" };
  const [draft, setDraft] = useState(() => ({
    ...postcard,
    location_label: { ...blankI18n, ...(postcard.location_label || {}) },
    title: { ...blankI18n, ...(postcard.title || {}) },
    greeting: { ...blankI18n, ...(postcard.greeting || {}) },
    body: { ...blankI18n, ...(postcard.body || {}) },
    signoff: { ...blankI18n, ...(postcard.signoff || {}) },
    caption: { ...blankI18n, ...(postcard.caption || {}) },
  }));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bunnyOpen, setBunnyOpen] = useState(false);

  const setI18n = (field, l, v) =>
    setDraft((d) => ({ ...d, [field]: { ...d[field], [l]: v } }));

  const I18nRow = ({ field, lines = 1, placeholder = "" }) => (
    <div className="space-y-1.5">
      {["en", "fr", "es"].map((l) => (
        <label key={l} className="flex items-start gap-2">
          <span className="mt-2 inline-block w-7 text-[10px] tracking-[0.2em] uppercase text-[#C16542] font-medium">
            {l.toUpperCase()}
          </span>
          {lines > 1 ? (
            <textarea
              value={draft[field]?.[l] || ""}
              onChange={(e) => setI18n(field, l, e.target.value)}
              rows={lines}
              placeholder={placeholder}
              className="flex-1 border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542]"
              data-testid={`postcard-editor-${field}-${l}`}
            />
          ) : (
            <input
              type="text"
              value={draft[field]?.[l] || ""}
              onChange={(e) => setI18n(field, l, e.target.value)}
              placeholder={placeholder}
              className="flex-1 border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542]"
              data-testid={`postcard-editor-${field}-${l}`}
            />
          )}
        </label>
      ))}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[80] bg-[#1A1513]/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-edit-allow="true"
      data-testid="postcard-editor"
    >
      <div
        className="bg-[#FDFBF7] w-full max-w-3xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-[#2C2621]/10 sticky top-0 bg-[#FDFBF7] z-10">
          <div>
            <p className="overline text-[#C16542]">Travel postcard</p>
            <h3 className="font-serif-x text-2xl tracking-tight">Edit postcard</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex w-9 h-9 items-center justify-center border border-[#2C2621]/15 hover:border-[#C16542]"
            aria-label="Close"
            data-testid="postcard-editor-close"
          >
            <X className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </header>

        <div className="px-6 py-5 space-y-5">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">Postmark / Postcard from… </p>
            <I18nRow field="location_label" placeholder="POSTCARD FROM MERZOUGA" />
          </div>
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">Handwritten title</p>
            <I18nRow field="title" placeholder="The sea of dunes" />
          </div>
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">Greeting</p>
            <I18nRow field="greeting" placeholder="Hello from the desert," />
          </div>
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">Body</p>
            <I18nRow field="body" lines={4} placeholder="We climbed onto a camel just as the sun was setting…" />
          </div>
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">Sign-off</p>
            <I18nRow field="signoff" placeholder="With sand in my shoes," />
          </div>
          <label className="block">
            <span className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">Signature</span>
            <input
              type="text"
              value={draft.signature || ""}
              onChange={(e) => setDraft((d) => ({ ...d, signature: e.target.value }))}
              placeholder="Marta"
              className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542]"
              data-testid="postcard-editor-signature"
            />
          </label>
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">Stamp caption</p>
            <I18nRow field="caption" placeholder="Erg Chebbi · Sahara" />
          </div>
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">Stamp image</p>
            <div className="flex flex-wrap items-center gap-3">
              <div className="w-24 h-28 bg-[#F3EBDD] border border-[#2C2621]/15 overflow-hidden">
                {draft.image_url ? (
                  <img src={draft.image_url} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async"/>
                ) : null}
              </div>
              <div className="flex-1 min-w-[200px] space-y-2">
                <input
                  type="url"
                  value={draft.image_url || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, image_url: e.target.value }))}
                  placeholder="https://…"
                  className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] font-mono"
                  data-testid="postcard-editor-image"
                />
                <button
                  type="button"
                  onClick={() => setBunnyOpen(true)}
                  className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-3 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors"
                  data-testid="postcard-editor-pick-image-bunny"
                  title="Browse the Bunny Storage Zone and pick a stamp image"
                >
                  <Database className="w-3.5 h-3.5" strokeWidth={1.6} />
                  Select from Bunny Store
                </button>
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-3 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors"
                  data-testid="postcard-editor-pick-image"
                >
                  Select from Library
                </button>
              </div>
            </div>
          </div>
        </div>

        <footer className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#2C2621]/10 bg-[#FBF6EE] sticky bottom-0">
          <button
            type="button"
            onClick={onClose}
            className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542] px-3 py-2"
            data-testid="postcard-editor-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(draft)}
            className="inline-flex items-center gap-2 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-5 py-2.5 text-[10px] tracking-[0.25em] uppercase transition-colors"
            data-testid="postcard-editor-save"
          >
            <Save className="w-3.5 h-3.5" strokeWidth={1.8} />
            Save changes
          </button>
        </footer>
      </div>

      {pickerOpen ? (
        <MediaPicker
          onClose={() => setPickerOpen(false)}
          onSelect={(asset) => {
            setDraft((d) => ({ ...d, image_url: asset?.url || "" }));
            setPickerOpen(false);
          }}
          title="Pick a stamp image"
          confirmLabel="Use this image"
        />
      ) : null}

      {bunnyOpen ? (
        <BunnyStoragePicker
          onClose={() => setBunnyOpen(false)}
          onSelect={(file) => {
            const f = Array.isArray(file) ? file[0] : file;
            setDraft((d) => ({ ...d, image_url: f?.public_url || "" }));
            setBunnyOpen(false);
          }}
          title="Pick a stamp image — Bunny Store"
        />
      ) : null}
    </div>
  );
};

// ── Main section ─────────────────────────────────────────────────────────
const DidYouKnowSection = ({ slug, propertyName }) => {
  const { lang } = useLanguage();
  const { textEditMode } = useEditMode();
  const [data, setData] = useState({ postcards: [], is_override: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/did-you-know/${slug}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData({
        postcards: Array.isArray(json?.postcards) ? json.postcards : [],
        is_override: !!json?.is_override,
      });
      setActiveIdx(0);
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const persist = useCallback(async (nextList) => {
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/did-you-know/${slug}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postcards: nextList }),
      });
      if (!res.ok) {
        const body = await safeReadBody(res);
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      const json = await res.json();
      const postcards = Array.isArray(json?.postcards) ? json.postcards : [];
      setData({ postcards, is_override: !!json?.is_override });
      // Clamp active index if list shrunk.
      setActiveIdx((cur) => Math.min(cur, Math.max(postcards.length - 1, 0)));
    } catch (e) {
      setError(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [slug]);

  const handleSavePostcard = (updated) => {
    const list = data.postcards.slice();
    const idx = list.findIndex((p) => p.id === updated.id);
    if (idx === -1) {
      list.push(updated);
      setEditing(null);
      persist(list);
      // Jump to the newly added card.
      setActiveIdx(list.length - 1);
    } else {
      list[idx] = updated;
      setEditing(null);
      persist(list);
    }
  };

  const handleDelete = (postcard) => {
    if (!window.confirm("Delete this postcard?")) return;
    persist(data.postcards.filter((p) => p.id !== postcard.id));
  };

  const handleMove = (idx, delta) => {
    const next = idx + delta;
    if (next < 0 || next >= data.postcards.length) return;
    const list = data.postcards.slice();
    const [item] = list.splice(idx, 1);
    list.splice(next, 0, item);
    setActiveIdx(next);
    persist(list);
  };

  const handleAdd = () => {
    const fresh = {
      id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `tmp-${Date.now()}`,
      kind: "culture",
      location_label: { en: "POSTCARD FROM …", fr: "CARTE POSTALE DE …", es: "POSTAL DESDE …" },
      title: { en: "", fr: "", es: "" },
      greeting: { en: "Hello,", fr: "Bonjour,", es: "Hola," },
      body: { en: "", fr: "", es: "" },
      signoff: { en: "Yours,", fr: "Bien à vous,", es: "Suyo," },
      signature: "",
      caption: { en: "", fr: "", es: "" },
      image_url: "",
    };
    setEditing(fresh);
  };

  const list = data.postcards;
  const safeIdx = Math.min(Math.max(activeIdx, 0), Math.max(list.length - 1, 0));
  const current = list[safeIdx];

  const goPrev = () => setActiveIdx((i) => (i - 1 + list.length) % list.length);
  const goNext = () => setActiveIdx((i) => (i + 1) % list.length);

  return (
    <section
      className="relative bg-[#FBF6EE] py-20 md:py-28 border-t border-[#2C2621]/10 overflow-hidden"
      data-testid="did-you-know-section"
    >
      {/* Soft pattern echo (very faint) */}
      <div
        className="absolute inset-0 moroccan-bg-zellige opacity-40 pointer-events-none"
        aria-hidden="true"
      />

      <div className="relative max-w-[1180px] mx-auto px-6 md:px-12">
        <header className="flex flex-wrap items-end justify-between gap-4 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <p className="overline text-[#C16542] inline-flex items-center gap-2">
              <Send className="w-3.5 h-3.5" strokeWidth={1.8} />
              {pick(
                { en: "Postcards from the road", fr: "Cartes postales du voyage", es: "Postales del viaje" },
                lang
              )}
            </p>
            <h2
              className="font-serif-x text-4xl md:text-5xl lg:text-6xl mt-3 tracking-tight leading-[1.05] text-[#1A1513]"
              data-testid="did-you-know-title"
            >
              {pick(
                { en: "Did You Know?", fr: "Le saviez-vous ?", es: "¿Sabía usted?" },
                lang
              )}
            </h2>
            {propertyName ? (
              <p className="mt-3 text-base text-[#5C5248] leading-relaxed">
                {pick(
                  {
                    en: `A small collection of travel notes sent from ${propertyName} — to read between two cups of mint tea.`,
                    fr: `Une petite collection de notes de voyage envoyées depuis ${propertyName} — à lire entre deux verres de thé à la menthe.`,
                    es: `Una pequeña colección de notas de viaje enviadas desde ${propertyName} — para leer entre dos vasos de té a la menta.`,
                  },
                  lang
                )}
              </p>
            ) : null}
          </div>

          {textEditMode ? (
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center gap-2 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-5 py-3 text-[10px] tracking-[0.25em] uppercase transition-colors"
              data-edit-allow="true"
              data-testid="did-you-know-add"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />
              Add postcard
            </button>
          ) : null}
        </header>

        {error ? (
          <p className="mb-4 text-sm text-[#C16542]" data-testid="did-you-know-error">{error}</p>
        ) : null}
        {saving ? (
          <div className="absolute right-12 top-12 text-[10px] tracking-[0.25em] uppercase text-[#5C5248] inline-flex items-center gap-2" data-testid="did-you-know-saving">
            <Loader2 className="w-3 h-3 animate-spin" /> Saving…
          </div>
        ) : null}
        {loading ? (
          <p className="text-sm text-[#5C5248]" data-testid="did-you-know-loading">Loading curiosities…</p>
        ) : null}

        {!loading && list.length === 0 ? (
          <div className="bg-white border border-[#2C2621]/10 p-8 text-sm text-[#5C5248]" data-testid="did-you-know-empty">
            No postcards yet.
            {textEditMode ? (
              <>{" "}<button type="button" onClick={handleAdd} className="text-[#C16542] hover:underline" data-edit-allow="true">Add the first one</button>.</>
            ) : null}
          </div>
        ) : null}

        {/* Slider container */}
        {!loading && list.length > 0 && current ? (
          <div className="relative" data-testid="did-you-know-slider">
            {/* Sliding stage — single postcard at a time with a soft cross-fade slide */}
            <div className="relative py-6">
              <PostcardArt key={current.id} postcard={current} lang={lang} indexTilt={safeIdx} />

              {/* Edit-mode action overlay (positioned on top of the postcard) */}
              {textEditMode ? (
                <div
                  className="absolute top-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 bg-[#FDFBF7]/95 border border-[#2C2621]/15 px-2 py-1.5 backdrop-blur-sm"
                  data-edit-allow="true"
                >
                  <button
                    type="button"
                    onClick={() => handleMove(safeIdx, -1)}
                    disabled={safeIdx === 0}
                    className="inline-flex w-7 h-7 items-center justify-center text-[#2C2621] hover:text-[#C16542] disabled:opacity-40"
                    title="Move left"
                    data-edit-allow="true"
                    data-testid={`postcard-move-up-${current.id}`}
                  >
                    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMove(safeIdx, +1)}
                    disabled={safeIdx === list.length - 1}
                    className="inline-flex w-7 h-7 items-center justify-center text-[#2C2621] hover:text-[#C16542] disabled:opacity-40"
                    title="Move right"
                    data-edit-allow="true"
                    data-testid={`postcard-move-down-${current.id}`}
                  >
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
                  </button>
                  <span className="w-px h-4 bg-[#2C2621]/15 mx-0.5" />
                  <button
                    type="button"
                    onClick={() => setEditing(current)}
                    className="inline-flex w-7 h-7 items-center justify-center text-[#2C2621] hover:text-[#C16542]"
                    title="Edit"
                    data-edit-allow="true"
                    data-testid={`postcard-edit-${current.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.8} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(current)}
                    className="inline-flex w-7 h-7 items-center justify-center text-[#C16542] hover:text-[#A35133]"
                    title="Delete"
                    data-edit-allow="true"
                    data-testid={`postcard-delete-${current.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                  </button>
                </div>
              ) : null}
            </div>

            {/* Floating nav arrows */}
            {list.length > 1 ? (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/3 md:translate-x-0 z-10 w-12 h-12 rounded-full bg-[#FDFBF7] border border-[#2C2621]/15 hover:border-[#C16542] hover:text-[#C16542] text-[#2C2621] shadow-[0_8px_18px_rgba(28,22,18,0.12)] transition-colors flex items-center justify-center"
                  aria-label="Previous postcard"
                  data-testid="did-you-know-prev"
                >
                  <ChevronLeft className="w-5 h-5" strokeWidth={1.8} />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/3 md:translate-x-0 z-10 w-12 h-12 rounded-full bg-[#FDFBF7] border border-[#2C2621]/15 hover:border-[#C16542] hover:text-[#C16542] text-[#2C2621] shadow-[0_8px_18px_rgba(28,22,18,0.12)] transition-colors flex items-center justify-center"
                  aria-label="Next postcard"
                  data-testid="did-you-know-next"
                >
                  <ChevronRight className="w-5 h-5" strokeWidth={1.8} />
                </button>
              </>
            ) : null}

            {/* Pagination dots */}
            {list.length > 1 ? (
              <div
                className="mt-10 flex items-center justify-center gap-2"
                data-testid="did-you-know-pagination"
              >
                {list.map((p, i) => (
                  <button
                    key={`dot-${p.id}`}
                    type="button"
                    onClick={() => setActiveIdx(i)}
                    className={`transition-all rounded-full ${
                      i === safeIdx
                        ? "w-8 h-2.5 bg-[#C16542]"
                        : "w-2.5 h-2.5 bg-[#2C2621]/20 hover:bg-[#2C2621]/40"
                    }`}
                    aria-label={`Show postcard ${i + 1}`}
                    aria-current={i === safeIdx}
                    data-testid={`did-you-know-dot-${i}`}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {editing ? (
        <PostcardEditor
          postcard={editing}
          onSave={handleSavePostcard}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </section>
  );
};

export default DidYouKnowSection;
